import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useToast } from '../../hooks/use-toast';
import { 
  Home, 
  Package, 
  Plus, 
  Settings, 
  Users, 
  DollarSign,
  ChevronRight,
  Eye,
  ShoppingBag,
  Calendar,
  Loader2,
  RefreshCw,
  AlertCircle,
  Lightbulb,
  TrendingUp,
  Menu,
  X,
  Star,
  Tag,
  Wallet,
  GraduationCap,
  HelpCircle,
  LogOut,
  Image,
  Truck,
  Palmtree,
  FileText,
  ClipboardList,
  CreditCard,
  Bell
} from 'lucide-react';
import PWAInstallPrompt from '../../components/lite/PWAInstallPrompt';
import LiteAddItemOverlay from '../../components/lite/LiteAddItemOverlay';
import LiteShipOverlay from '../../components/lite/LiteShipOverlay';
import RazorpaySetupCTA from '../../components/vendor/RazorpaySetupCTA';
import VendorWalletCard from '../../components/vendor/VendorWalletCard';

const LiteDashboard = ({ onToggleMode }) => {
  const navigate = useNavigate();
  const { user, apiCall, logout } = useAuth();
  const { formatPrice } = useCurrency();
  const { toast } = useToast();
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isShipping, setIsShipping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [isOnHoliday, setIsOnHoliday] = useState(false);
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  // iter-231-R — Toast the auto-trial confirmation ONCE when the user
  // lands on the dashboard with `auto_trial_started=true` on their
  // user doc. Marker is cleared via localStorage so a re-login or a
  // refresh doesn't re-toast. The backend set this flag when the
  // Smart Resume resolver auto-assigned the default trial because the
  // vendor skipped (or navigated back from) plan selection.
  useEffect(() => {
    if (!user?.auto_trial_started) return;
    const seenKey = `aniyor.auto_trial_toast_seen.${user.id}`;
    if (typeof window !== 'undefined' && window.localStorage?.getItem(seenKey)) return;
    const planName = user?.subscription_plan || 'Basic';
    const endsAt = user?.trial_ends_at
      ? new Date(user.trial_ends_at).toLocaleDateString()
      : 'in 30 days';
    toast({
      title: `You're on a free ${planName} trial`,
      description: `No card charged. Pick any plan from Settings → Subscription anytime — your trial runs through ${endsAt}.`,
      duration: 8000,
    });
    if (typeof window !== 'undefined') {
      window.localStorage?.setItem(seenKey, '1');
    }
  }, [user?.auto_trial_started, user?.id, user?.subscription_plan, user?.trial_ends_at, toast]);

  // Determine user intent from onboarding profile
  const userIntent = user?.onboarding_profile?.intents?.[0] || 'products';
  const isProductSeller = userIntent === 'products';
  const userName = user?.name?.split(' ')[0] || 'Seller';
  const businessName = user?.vendor_profile?.store_name || 
                       user?.identity?.store_name ||
                       user?.tax_info?.trade_name || 
                       'Your Store';

  const fetchDashboardData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      // Fetch vendor stats + orders + notifications
      const [statsResponse, ordersResponse, notificationsResponse] = await Promise.all([
        apiCall('/api/dashboard/vendor/stats?preset=last_30_days'),
        apiCall('/api/orders/'),
        apiCall('/api/dashboard/vendor/notifications').catch(() => ({ notifications: [], unseen_count: 0 })),
      ]);

      // Calculate weekly earnings from revenue
      const weeklyEarnings = statsResponse?.overview?.revenue?.total || 0;
      const pendingOrders = statsResponse?.pending_actions?.find(a => a.type === 'orders_pending')?.count || 0;
      
      // Get pending orders list — the orders API returns a flat array
      const rawOrders = Array.isArray(ordersResponse) ? ordersResponse : (ordersResponse?.orders || ordersResponse?.items || []);
      const pendingOrdersList = rawOrders.filter(o => 
        ['pending', 'confirmed', 'processing', 'unfulfilled'].includes(o.status)
      ).slice(0, 5);

      setDashboardData({
        earnings_week: weeklyEarnings,
        pending_orders: pendingOrders || pendingOrdersList.length,
        pending_orders_list: pendingOrdersList,
        total_listings: statsResponse?.overview?.listings?.total || 0,
        active_listings: statsResponse?.overview?.listings?.active || 0,
        total_orders: rawOrders.length || statsResponse?.overview?.orders?.total || 0,
        rating: statsResponse?.overview?.rating?.average || 0,
        review_count: statsResponse?.overview?.rating?.count || 0,
        pending_actions: statsResponse?.pending_actions || [],
        top_products: statsResponse?.top_products || [],
        unread_notifications: notificationsResponse?.unseen_count || 0,
        latest_notification_title: notificationsResponse?.notifications?.[0]?.title || '',
        suggestions: []
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Set fallback data
      setDashboardData({
        earnings_week: 0,
        pending_orders: 0,
        pending_orders_list: [],
        total_listings: 0,
        active_listings: 0,
        total_orders: 0,
        rating: 0,
        review_count: 0,
        pending_actions: [],
        top_products: [],
        suggestions: []
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch holiday mode status
  useEffect(() => {
    const fetchHolidayStatus = async () => {
      try {
        const response = await apiCall('/api/vendors/me/holiday-mode');
        setIsOnHoliday(response?.is_on_holiday || false);
      } catch (error) {
        console.error('Failed to fetch holiday status:', error);
      }
    };
    fetchHolidayStatus();
  }, [apiCall]);

  // Check onboarding completion status
  // iter-230af — Hard one-shot guard per (vendor_id) tuple. Without
  // this the effect re-fires every time `apiCall`'s identity changes
  // (which happens on every token rotation, every silent refresh, and
  // — in production — every time the Emergent platform's auto-refresh
  // bumps the cookie). Each re-fire issued 3 parallel API calls, and
  // when `registration-progress` 403'd on a half-baked PENDING_ONBOARDING
  // vendor the failures cascaded into a 10+ visible request loop in
  // DevTools. The ref makes us idempotent per user; the explicit 4xx
  // back-off keeps us idempotent even if the ref is bypassed by a
  // remount.
  const onboardingChecksRef = useRef({ ranForUserId: null, lastFailedAt: 0 });
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user?.id) return;
      const ref = onboardingChecksRef.current;
      // Already ran successfully for this user in this mount — skip.
      if (ref.ranForUserId === user.id) return;
      // Cool-down after a recent 4xx failure (10 min) so we don't
      // hammer the API while the vendor's state is fixed server-side.
      if (ref.lastFailedAt && Date.now() - ref.lastFailedAt < 10 * 60 * 1000) {
        return;
      }
      try {
        const [regResponse, profileResp, onboardingResp] = await Promise.all([
          apiCall(`/api/vendor-tax-registration/registration-progress/${user.id}`),
          apiCall('/api/vendors/complete-profile/me'),
          apiCall('/api/vendors/onboarding-status')
        ]);

        const hasBank = !!regResponse?.bank_account;
        const hasTax = !!regResponse?.tax_registration;
        const isOnboardingDone = regResponse?.onboarding_completed === true || user?.onboarding_completed === true;
        const hasSub = !!regResponse?.subscription;
        const subSkipped = regResponse?.subscription_selection_skipped === true;

        const steps = [];
        if (!hasTax) steps.push({ label: 'Business & Tax Registration', route: '/vendor/tax-registration' });
        if (!hasBank && !isOnboardingDone) steps.push({ label: 'Bank Details', route: '/vendor/payouts' });
        if (!isOnboardingDone && !hasSub && !subSkipped) {
          steps.push({ label: 'Store Setup & Subscription', route: '/vendor/tax-registration' });
        }

        const docs = profileResp?.documents || {};
        const hasGst = docs.gst_certificate;
        const hasPan = docs.pan_card;
        if (!hasGst && !hasPan) steps.push({ label: 'Upload KYC Documents', route: '/profile' });

        // Shipping setup step
        if (!onboardingResp?.shipping_configured) {
          steps.push({ label: 'Set Up Shipping', route: '/settings/shipping' });
        }

        const completed = steps.length === 0;
        const totalPossible = 5; // tax, bank, store+sub, kyc, shipping
        const progress = Math.max(0, Math.min(100, Math.round(((totalPossible - steps.length) / totalPossible) * 100)));

        setOnboardingStatus({ steps, completed, progress });
        ref.ranForUserId = user.id;
        ref.lastFailedAt = 0;
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        // Tag this as a 4xx (apiCall rejects with an Error whose message
        // includes "403" / "forbidden" / "401" / "404"). Block retries
        // for 10 minutes so the same broken state can't loop the API.
        ref.lastFailedAt = Date.now();
      }
    };
    checkOnboardingStatus();
    // NOTE: dep array intentionally narrow. `apiCall` regenerates on
    // every token rotation; depending on it restarted the cascade.
    // `user?.registration_step` is not read inside the effect.
  }, [user?.id]);

  // Toggle holiday mode
  const toggleHolidayMode = async () => {
    setHolidayLoading(true);
    try {
      const response = await apiCall('/api/vendors/me/holiday-mode', {
        method: 'POST',
        body: JSON.stringify({
          is_on_holiday: !isOnHoliday
        })
      });
      
      setIsOnHoliday(response.is_on_holiday);
      toast({
        title: response.is_on_holiday ? '🏖️ Holiday Mode On' : '✅ Store Active',
        description: response.message,
        duration: 4000
      });
    } catch (error) {
      console.error('Failed to toggle holiday mode:', error);
      toast({
        title: 'Error',
        description: 'Could not update holiday mode. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setHolidayLoading(false);
    }
  };

  const handleShipOrder = (order = null) => {
    setSelectedOrder(order);
    setIsShipping(true);
  };

  // Tips based on user activity
  const getTip = () => {
    const tips = [
      { title: "Tip of the Day", text: "Adding 3 photos instead of 1 increases sales by 40%. Try editing your listings!" },
      { title: "Pro Tip", text: "Respond to customer queries within 2 hours to boost your store rating." },
      { title: "Did You Know?", text: "Listings with detailed descriptions get 2x more views." },
      { title: "Growth Hack", text: "Offer free shipping on orders above ₹500 to increase average order value." }
    ];
    return tips[Math.floor(Math.random() * tips.length)];
  };

  const tip = getTip();

  if (loading) {
    return (
      <div className="min-h-screen bg-orange-50/30 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-orange-50/30 font-sans pb-20">
      
      {/* PWA Prompt */}
      <PWAInstallPrompt />

      {/* OVERLAYS */}
      <LiteAddItemOverlay 
        isOpen={isAddingItem} 
        onClose={() => setIsAddingItem(false)} 
        intent={userIntent}
        onSuccess={() => fetchDashboardData(true)}
      />

      <LiteShipOverlay 
        isOpen={isShipping} 
        onClose={() => setIsShipping(false)}
        order={selectedOrder}
      />

      {/* Friendly Header */}
      <header className="bg-white px-4 sm:px-6 py-4 shadow-sm flex justify-between items-center sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold text-lg">
            {userName[0]?.toUpperCase()}
          </div>
          <div>
            <h1 className="font-bold text-gray-800 text-lg leading-tight">Namaste, {userName}</h1>
            <p className="text-xs text-gray-500 truncate max-w-[180px]">{businessName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => fetchDashboardData(true)}
            disabled={refreshing}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <button 
            onClick={onToggleMode}
            className="text-gray-500 hover:text-indigo-600 flex items-center gap-1 text-sm font-medium"
          >
            <Settings size={16} /> Advanced
          </button>
        </div>
      </header>

      {/* Holiday Mode Banner */}
      <div className={`px-4 sm:px-6 py-3 flex items-center justify-between ${isOnHoliday ? 'bg-amber-50 border-b border-amber-200' : 'bg-gray-50 border-b border-gray-100'}`}>
        <div className="flex items-center gap-2">
          <Palmtree size={18} className={isOnHoliday ? 'text-amber-600' : 'text-gray-400'} />
          <span className={`text-sm font-medium ${isOnHoliday ? 'text-amber-800' : 'text-gray-600'}`}>
            {isOnHoliday ? 'Store Paused (Holiday Mode)' : 'Holiday Mode'}
          </span>
        </div>
        {/* Fixed Toggle Switch */}
        <button
          type="button"
          role="switch"
          aria-checked={isOnHoliday}
          onClick={toggleHolidayMode}
          disabled={holidayLoading}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
            isOnHoliday ? 'bg-amber-500' : 'bg-slate-200'
          } ${holidayLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <span 
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${
              isOnHoliday ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Holiday Mode Active Alert */}
      {isOnHoliday && (
        <div className="mx-4 sm:mx-6 mt-3 p-3 bg-amber-100 border border-amber-200 rounded-xl">
          <p className="text-sm text-amber-800 flex items-center gap-2">
            <AlertCircle size={16} />
            Your store is paused. Customers won&apos;t be able to place new orders. Toggle off to resume.
          </p>
        </div>
      )}

      <main className="p-4 sm:p-6 max-w-lg mx-auto space-y-6">
        
        {/* iter-230bj — Vendor-side Razorpay payout setup CTA. Renders
            conditionally based on /api/vendor/razorpay/setup-status. */}
        <RazorpaySetupCTA />

        {/* iter-230bv — Shipping Pool Wallet card. Tap-through to a full
            ledger page lives on /vendor/wallet. Renders gracefully when
            no wallet data is present yet. */}
        <VendorWalletCard onViewLedger={() => navigate('/vendor/wallet')} />

        {/* 1. Money Card (Motivation) */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-5 sm:p-6 text-white shadow-lg relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-indigo-100 text-sm font-medium mb-1">Your Earnings this week</p>
            <h2 className="text-3xl sm:text-4xl font-bold">{formatPrice(dashboardData?.earnings_week || 0)}</h2>
            <p className="text-xs text-indigo-200 mt-2 opacity-80">Keep it up! Payment arrives Tuesday.</p>
          </div>
          <DollarSign className="absolute right-[-20px] bottom-[-20px] text-white opacity-10 w-28 h-28 sm:w-32 sm:h-32" />
        </div>

        {/* 2. Action Center (What needs attention NOW) */}
        {dashboardData?.pending_orders > 0 ? (
          <div className="bg-white rounded-xl border border-orange-200 shadow-sm p-4 sm:p-5 flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="bg-orange-100 text-orange-600 p-2.5 sm:p-3 rounded-full">
                <Package size={22} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base sm:text-lg">{dashboardData.pending_orders} New Orders!</h3>
                <p className="text-sm text-gray-500">Customers are waiting.</p>
              </div>
            </div>
            <button 
              onClick={() => handleShipOrder(dashboardData?.pending_orders_list?.[0])}
              className="bg-orange-600 text-white px-4 sm:px-5 py-2 rounded-full font-bold text-sm shadow hover:bg-orange-700 transition-transform active:scale-95"
            >
              Ship Now
            </button>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center text-green-800 text-sm">
            <span className="inline-flex items-center gap-2">
              <span className="text-lg">✓</span>
              All caught up! No pending orders.
            </span>
          </div>
        )}

        {/* 2b. Notifications Card (below New Orders) */}
        {dashboardData?.unread_notifications > 0 && (
          <div className="bg-white rounded-xl border border-blue-200 shadow-sm p-4 sm:p-5 flex items-center justify-between" data-testid="lite-notifications-card">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="bg-blue-100 text-blue-600 p-2.5 sm:p-3 rounded-full">
                <Bell size={22} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base sm:text-lg">{dashboardData.unread_notifications} Notification{dashboardData.unread_notifications > 1 ? 's' : ''}</h3>
                <p className="text-sm text-gray-500">{dashboardData.latest_notification_title || 'New alerts for you.'}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/vendor/notifications')}
              className="bg-blue-600 text-white px-4 sm:px-5 py-2 rounded-full font-bold text-sm shadow hover:bg-blue-700 transition-transform active:scale-95"
              data-testid="lite-view-notifications-btn"
            >
              View
            </button>
          </div>
        )}

        {/* 3. Quick Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white p-3 rounded-xl border border-gray-100 text-center">
            <p className="text-2xl font-bold text-gray-800">{dashboardData?.total_listings || 0}</p>
            <p className="text-xs text-gray-500">Listings</p>
          </div>
          <div className="bg-white p-3 rounded-xl border border-gray-100 text-center">
            <p className="text-2xl font-bold text-gray-800">{dashboardData?.total_orders || 0}</p>
            <p className="text-xs text-gray-500">Orders</p>
          </div>
          <div className="bg-white p-3 rounded-xl border border-gray-100 text-center">
            <p className="text-2xl font-bold text-gray-800 flex items-center justify-center gap-1">
              {dashboardData?.rating || 0}
              <span className="text-yellow-500 text-lg">★</span>
            </p>
            <p className="text-xs text-gray-500">Rating</p>
          </div>
        </div>

        {/* 4. The "Big Buttons" Grid */}
        <div className="grid grid-cols-2 gap-4">
          <button 
            onClick={() => setIsAddingItem(true)}
            className="bg-white p-4 sm:p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all text-left group"
          >
            <div className="bg-blue-50 text-blue-600 w-10 h-10 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Plus size={24} />
            </div>
            <span className="font-bold text-gray-800 block">Sell New Item</span>
            <span className="text-xs text-gray-400">Add photo & price</span>
          </button>

          <button 
            onClick={() => navigate('/listings')}
            className="bg-white p-4 sm:p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all text-left group"
          >
            <div className="bg-purple-50 text-purple-600 w-10 h-10 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              {isProductSeller ? <ShoppingBag size={24} /> : <Calendar size={24} />}
            </div>
            <span className="font-bold text-gray-800 block">{isProductSeller ? 'My Shop Shelf' : 'My Bookings'}</span>
            <span className="text-xs text-gray-400">View what's listed</span>
          </button>
        </div>

        {/* 5. Pending Actions (if any besides orders) */}
        {dashboardData?.pending_actions?.length > 1 && (
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h4 className="font-bold text-gray-800 text-sm mb-3 flex items-center gap-2">
              <AlertCircle size={16} className="text-orange-500" />
              Needs Attention
            </h4>
            <div className="space-y-2">
              {dashboardData.pending_actions.slice(0, 3).filter(a => a.type !== 'orders_pending').map((action, idx) => (
                <div 
                  key={action.type || action.title || `action-${idx}`}
                  onClick={() => navigate(action.action_url?.replace('/vendor', '') || '/')}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <span className="text-sm text-gray-700">{action.title}</span>
                  <div className="flex items-center gap-2">
                    <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-bold">
                      {action.count}
                    </span>
                    <ChevronRight size={16} className="text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 6. Top Selling Products */}
        {dashboardData?.top_products?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h4 className="font-bold text-gray-800 text-sm mb-3 flex items-center gap-2">
              <TrendingUp size={16} className="text-green-500" />
              Top Sellers This Week
            </h4>
            <div className="space-y-2">
              {dashboardData.top_products.slice(0, 3).map((product, idx) => (
                <div 
                  key={product.id || product.name || `product-${idx}`}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-400 w-5">#{idx + 1}</span>
                    <span className="text-sm text-gray-700 truncate max-w-[150px]">{product.title}</span>
                  </div>
                  <span className="text-sm font-bold text-green-600">{formatPrice(product.revenue || 0)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 7. Quick Help / Tips */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex gap-3 items-start">
          <div className="bg-yellow-100 text-yellow-700 p-2 rounded-lg shrink-0">
            <Lightbulb size={18} />
          </div>
          <div>
            <h4 className="font-bold text-gray-800 text-sm">{tip.title}</h4>
            <p className="text-xs text-gray-500 mt-1">
              {tip.text}
            </p>
          </div>
        </div>

        {/* 8. Onboarding Completion Banner */}
        {onboardingStatus && !onboardingStatus.completed && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-4 sm:p-5" data-testid="onboarding-banner">
            <div className="flex items-start gap-3 mb-3">
              <div className="bg-amber-100 text-amber-600 p-2 rounded-full shrink-0">
                <AlertCircle size={20} />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-amber-900 text-sm">Complete Your Store Setup</h4>
                <p className="text-xs text-amber-700 mt-1">
                  Finish these steps to go live and start receiving orders
                </p>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-amber-800">{onboardingStatus.progress}% complete</span>
                <span className="text-xs text-amber-600">{onboardingStatus.steps.length} steps remaining</span>
              </div>
              <div className="w-full bg-amber-200 rounded-full h-2">
                <div 
                  className="bg-amber-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${onboardingStatus.progress}%` }}
                />
              </div>
            </div>

            {/* Pending Steps */}
            <div className="space-y-2 mb-3">
              {onboardingStatus.steps.map((step, idx) => (
                <div 
                  key={step.key || step.label || `step-${idx}`}
                  onClick={() => navigate(step.route)}
                  className="flex items-center gap-2 p-2 bg-white rounded-lg border border-amber-100 cursor-pointer hover:bg-amber-50 transition-colors"
                >
                  <div className="w-5 h-5 rounded-full border-2 border-amber-400 flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-amber-600">{idx + 1}</span>
                  </div>
                  <span className="text-sm text-amber-900 font-medium flex-1">{step.label}</span>
                  <ChevronRight size={16} className="text-amber-400" />
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate(onboardingStatus.steps[0]?.route || '/vendor/tax-registration')}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-2.5 px-4 rounded-xl text-sm transition-colors active:scale-[0.98]"
              data-testid="complete-setup-btn"
            >
              Continue Setup
            </button>
          </div>
        )}

        {/* 8. Quick Links */}
        <div className="grid grid-cols-3 gap-3">
          <button 
            onClick={() => navigate('/orders')}
            className="bg-white p-3 rounded-xl border border-gray-100 text-center hover:bg-gray-50 transition-colors"
          >
            <Package size={20} className="mx-auto text-gray-600 mb-1" />
            <span className="text-xs text-gray-600">All Orders</span>
          </button>
          <button 
            onClick={() => navigate('/vendor/payouts')}
            className="bg-white p-3 rounded-xl border border-gray-100 text-center hover:bg-gray-50 transition-colors"
          >
            <DollarSign size={20} className="mx-auto text-gray-600 mb-1" />
            <span className="text-xs text-gray-600">Payouts</span>
          </button>
          <button 
            onClick={() => navigate('/analytics')}
            className="bg-white p-3 rounded-xl border border-gray-100 text-center hover:bg-gray-50 transition-colors"
          >
            <Eye size={20} className="mx-auto text-gray-600 mb-1" />
            <span className="text-xs text-gray-600">Analytics</span>
          </button>
        </div>

      </main>

      {/* Hamburger Menu Overlay */}
      {showMenu && (
        <div className="fixed inset-0 z-50 bg-white animate-in slide-in-from-right-0 duration-200">
          {/* Menu Header */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold text-lg">
                {userName[0]?.toUpperCase()}
              </div>
              <div>
                <p className="font-semibold">{userName}</p>
                <p className="text-xs text-indigo-100 truncate max-w-[150px]">{businessName}</p>
              </div>
            </div>
            <button 
              onClick={() => setShowMenu(false)}
              className="p-2 hover:bg-white/20 rounded-full"
            >
              <X size={24} />
            </button>
          </div>
          
          {/* Menu Items */}
          <div className="overflow-y-auto h-[calc(100vh-140px)] pb-20">
            <div className="p-4 space-y-1">
              {/* Daily Operations */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-2 pt-2">Daily Operations</p>
              
              <button onClick={() => { navigate('/orders'); setShowMenu(false); }} 
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Package size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">All Orders</span>
              </button>
              
              <button onClick={() => { navigate('/orders/leads'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Users size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Leads & Inquiries</span>
              </button>

              <button onClick={() => { navigate('/vendor/requests'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Users size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Customer Requests</span>
              </button>
              
              <button onClick={() => { navigate('/bookings'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Calendar size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Bookings</span>
              </button>
              
              <button onClick={() => { navigate('/vendor/payouts'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Wallet size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Payouts & Finance</span>
              </button>
              
              <button onClick={() => { navigate('/orders/shipping'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Truck size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Pack & Ship</span>
              </button>

              <button onClick={() => { navigate('/vendor/tax-invoices'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <FileText size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Tax Invoices</span>
              </button>
              
              {/* Catalog */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-2 pt-4">Catalog</p>
              
              <button onClick={() => { navigate('/listings'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <ShoppingBag size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">All Listings</span>
              </button>

              <button onClick={() => { navigate('/orders/inventory'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <ClipboardList size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Inventory</span>
              </button>
              
              <button onClick={() => { navigate('/listings/reviews'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Star size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Reviews</span>
              </button>
              
              <button onClick={() => { navigate('/media'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Image size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Media Gallery</span>
              </button>
              
              {/* Grow */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-2 pt-4">Grow</p>
              
              <button onClick={() => { navigate('/analytics'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Eye size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Analytics</span>
              </button>
              
              <button onClick={() => { navigate('/offers'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Tag size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Discounts</span>
              </button>
              
              {/* Learn & Settings */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-2 pt-4">Learn & Settings</p>
              
              <button onClick={() => { navigate('/vendor/training'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors">
                <GraduationCap size={20} className="text-blue-600" />
                <span className="font-medium text-blue-900">Training Academy</span>
              </button>
              
              <button onClick={() => { navigate('/help'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <HelpCircle size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Help & Support</span>
              </button>
              
              <button onClick={() => { navigate('/vendor/store-profile'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Users size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Store Profile</span>
              </button>

              <button onClick={() => { navigate('/vendor/subscription-plans'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <CreditCard size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Subscription</span>
              </button>

              <button onClick={() => { navigate('/vendor/billing'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <CreditCard size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Billing</span>
              </button>

              <button onClick={() => { navigate('/vendor/notifications'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors">
                <Bell size={20} className="text-gray-600" />
                <span className="font-medium text-gray-900">Notifications</span>
              </button>
              
              {/* Advanced Mode */}
              <div className="pt-4 border-t border-gray-200 mt-4">
                <button 
                  onClick={() => { onToggleMode(); setShowMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <Settings size={20} className="text-gray-600" />
                  <span className="font-medium text-gray-900">Switch to Advanced Mode</span>
                </button>
              </div>
            </div>
          </div>
          
          {/* Logout Button */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
            <button 
              onClick={() => { logout(); navigate('/login'); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={18} />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      )}

      {/* Simple Bottom Nav (Mobile App Feel) - With Hamburger Menu */}
      <nav className="fixed bottom-0 w-full bg-white border-t border-gray-200 flex justify-around py-3 px-2 text-xs font-medium text-gray-400 z-20 safe-area-pb">
        <button className="flex flex-col items-center gap-1 text-indigo-600">
          <Home size={20} />
          Home
        </button>
        <button 
          onClick={() => navigate('/orders')}
          className="flex flex-col items-center gap-1 hover:text-gray-600 relative"
        >
          <div className="relative">
            <Package size={20} />
            {dashboardData?.pending_orders > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                {dashboardData.pending_orders > 9 ? '9+' : dashboardData.pending_orders}
              </span>
            )}
          </div>
          Orders
        </button>
        <button 
          onClick={() => navigate('/bookings')}
          className="flex flex-col items-center gap-1 hover:text-gray-600"
        >
          <Calendar size={20} />
          Bookings
        </button>
        <button 
          onClick={() => navigate('/listings')}
          className="flex flex-col items-center gap-1 hover:text-gray-600"
        >
          <ShoppingBag size={20} />
          Listings
        </button>
        <button 
          onClick={() => setShowMenu(true)}
          className="flex flex-col items-center gap-1 hover:text-gray-600"
        >
          <Menu size={20} />
          More
        </button>
      </nav>
    </div>
  );
};

export default LiteDashboard;
